<script setup lang="ts">
import { useI18n } from 'vue-i18n'

import AppIcon from '@/components/ui/AppIcon.vue'
import type { SavedAddress } from '@/composables/useAddresses'

/**
 * Сохранённые адреса списком. Строка сделана не из `ListRow`: там всё
 * завёрнуто в одну кнопку, а здесь их две — выбрать адрес и открыть его
 * правку, — и кнопку в кнопку вкладывать нельзя.
 */
interface Props {
  addresses: SavedAddress[]
  /** Показывать карандаш правки: в онбординге адрес только выбирают */
  editable?: boolean
}

withDefaults(defineProps<Props>(), { editable: true })

const emit = defineEmits<{
  select: [address: SavedAddress]
  edit: [address: SavedAddress]
}>()

const { t } = useI18n()
</script>

<template>
  <div class="saved-address">
    <div v-for="address in addresses" :key="address.id" class="saved-address__row">
      <button type="button" class="saved-address__pick" @click="emit('select', address)">
        <span class="saved-address__icon">
          <AppIcon name="row-address" :size="24" />
        </span>
        <span class="saved-address__text">
          <span class="saved-address__title font-13-b">
            {{ address.label || t('address.label') }}
          </span>
          <span class="saved-address__subtitle font-13-m">{{ address.name }}</span>
        </span>
      </button>

      <button
        v-if="editable"
        type="button"
        class="saved-address__edit"
        :aria-label="t('address.edit')"
        @click="emit('edit', address)"
      >
        <AppIcon name="edit" :size="24" />
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.saved-address {
  display: flex;
  flex-direction: column;
  gap: 10px;

  /* Плашка та же, что у `ListRow`: 50px, r20, фон #F7F7F7 */
  &__row {
    display: flex;
    align-items: center;
    border-radius: var(--radius-xl);
    background: var(--surface-muted);
  }

  &__pick {
    display: flex;
    flex: 1;
    align-items: center;
    gap: 10px;
    min-width: 0;
    padding: 4px;
    border: 0;
    background: none;
    text-align: left;
    cursor: pointer;
  }

  &__icon {
    display: flex;
    flex: 0 0 42px;
    align-items: center;
    justify-content: center;
    height: 42px;
    border-radius: var(--radius-l);
    background: var(--surface-card);
    color: var(--icon-color);
  }

  &__text {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  /*
   * Обе строки в одну линию: без названия в заголовок попадает сам адрес,
   * а он бывает длинным — иначе плашка вырастает на три строки.
   */
  &__title,
  &__subtitle {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__title {
    color: var(--primary-500);
  }

  &__subtitle {
    color: var(--text-color);
  }

  &__edit {
    display: flex;
    flex: none;
    padding: 0 13px;
    border: 0;
    background: none;
    color: var(--icon-color);
    cursor: pointer;
  }
}
</style>
